import { ShoppingBagOutlined } from '@mui/icons-material';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';

import { SearchHitPurchaseRequisition } from '../../components/SearchHitPurchaseRequisition/SearchHitPurchaseRequisition';
import { SearchRefinementListItemCuriosity } from '../../components/SearchRefinementListItemCuriosity/SearchRefinementListItemCuriosity';
import { SearchRefinementListItemI18n } from '../../components/SearchRefinementListItemI18n/SearchRefinementListItemI18n';
import { SearchDateRange } from '../../filters/SearchDateRange/SearchDateRange';
import { SearchNumberRange } from '../../filters/SearchNumberRange/SearchNumberRange.tsx';
import { SearchRefinementList } from '../../filters/SearchRefinementList/SearchRefinementList';
import { FacetVariant } from '../../types/FacetVariant.ts';
import { SearchConfigItem } from '../../types/SearchConfigItem';
import { SearchFilterAttribute } from '../../types/SearchFilterAttribute';
import { SearchIndex } from '../../types/SearchIndex';
import { SearchIndexConfigFactoryOptions } from '../../types/SearchIndexConfigFactoryOptions';
import { createSearchFilterAttributeItem } from '../../util/createSearchFilterAttributeItem';
import { toNumberRangeRouteValue } from '../../util/mapStateToRoute.ts';
import { createSearchSortByItemValue } from '~/routes/Search/util/createSearchSortByItemValue.ts';

const indexName = SearchIndex.PURCHASE_REQUISITION;

export const purchaseRequisitionSearchIndexConfig = ({ t }: SearchIndexConfigFactoryOptions): SearchConfigItem => {
  return {
    title: t('indexes.purchaseRequisition.title'),
    description: t('indexes.purchaseRequisition.description'),
    icon: ShoppingBagOutlined,
    indexName,
    slug: 'prq',
    hitComponent: SearchHitPurchaseRequisition,
    filters: [
      createSearchFilterAttributeItem({
        attribute: SearchFilterAttribute.PRICE_TOTAL,
        slug: 'price',
        facetVariant: FacetVariant.RANGE,
        component: SearchNumberRange,
        componentProps: {
          title: t('filter.priceTotal.title'),
        },
        valueStateToRoute: toNumberRangeRouteValue,
      }),
      createSearchFilterAttributeItem({
        attribute: SearchFilterAttribute.CREATED_AT,
        slug: 'created',
        facetVariant: FacetVariant.RANGE,
        component: SearchDateRange,
        componentProps: {
          title: t('filter.createdAt.title'),
        },
        valueStateToRoute: toNumberRangeRouteValue,
      }),
      createSearchFilterAttributeItem({
        attribute: SearchFilterAttribute.REQUESTED_AT,
        slug: 'requested',
        facetVariant: FacetVariant.RANGE,
        component: SearchDateRange,
        componentProps: {
          title: t('filter.requestedAt.title'),
        },
        valueStateToRoute: toNumberRangeRouteValue,
      }),
      createSearchFilterAttributeItem({
        attribute: SearchFilterAttribute.STATUS,
        slug: 'status',
        facetVariant: FacetVariant.REFINEMENT_LIST,
        component: SearchRefinementList,
        componentProps: {
          title: t('filter.status.title'),
          itemComponent: SearchRefinementListItemI18n,
          itemComponentProps: {
            keyPrefix: 'PurchaseRequisitionStatus',
          },
        },
      }),
      createSearchFilterAttributeItem({
        attribute: SearchFilterAttribute.SUPPLIER_ID,
        slug: 'supplier',
        facetVariant: FacetVariant.REFINEMENT_LIST,
        component: SearchRefinementList,
        componentProps: {
          title: t('filter.supplier.title'),
          itemComponent: SearchRefinementListItemCuriosity,
          itemComponentProps: {
            ns: CuriosityNamespaceType.SUPPLIER,
          },
        },
      }),
      createSearchFilterAttributeItem({
        attribute: SearchFilterAttribute.COMMODITY_GROUP_ID,
        slug: 'commodity-group',
        facetVariant: FacetVariant.REFINEMENT_LIST,
        component: SearchRefinementList,
        componentProps: {
          title: t('filter.commodityGroup.title'),
          itemComponent: SearchRefinementListItemCuriosity,
          itemComponentProps: {
            ns: CuriosityNamespaceType.COMMODITY_GROUP,
          },
        },
      }),
    ],
    sortByItems: [
      {
        label: t('sortBy.relevance'),
        value: createSearchSortByItemValue(indexName),
        slug: 'relevance',
        isDefault: true,
      },
      {
        label: t('sortBy.createdAtAsc'),
        value: createSearchSortByItemValue(indexName, SearchFilterAttribute.CREATED_AT),
        slug: 'created-at:asc',
      },
      {
        label: t('sortBy.createdAtDesc'),
        value: createSearchSortByItemValue(indexName, SearchFilterAttribute.CREATED_AT, true),
        slug: 'created-at:desc',
      },
      {
        label: t('sortBy.priceTotalAsc'),
        value: createSearchSortByItemValue(indexName, SearchFilterAttribute.PRICE_TOTAL),
        slug: 'price:asc',
      },
      {
        label: t('sortBy.priceTotalDesc'),
        value: createSearchSortByItemValue(indexName, SearchFilterAttribute.PRICE_TOTAL, true),
        slug: 'price:desc',
      },
      {
        label: t('sortBy.priceUnitAsc'),
        value: createSearchSortByItemValue(indexName, SearchFilterAttribute.PRICE_UNIT),
        slug: 'price-unit:asc',
      },
      {
        label: t('sortBy.priceUnitDesc'),
        value: createSearchSortByItemValue(indexName, SearchFilterAttribute.PRICE_UNIT, true),
        slug: 'price-unit:desc',
      },
    ],
  };
};
